"use client";

import React, { useState, useEffect, Fragment } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function normalizeMenuHref(href: string | null | undefined) {
  if (!href) return "#";
  const routeMap: Record<string, string> = {
    "/HomePage": "/",
    "/PricingPage": "/pricing",
    "/WhiteLabelForAgenciesPage": "/white-label",
    "/CapacityPacksPage": "/capacity-packs",
    "/SecurityAndGovernancePage": "/security-governance",
    "/BookADemoContactSalesPage": "/book-a-demo",
    "/pricing": "/pricing",
    "/white-label": "/white-label",
    "/capacity-packs": "/capacity-packs",
    "/security-governance": "/security-governance",
    "/book-a-demo": "/book-a-demo",
    "/": "/"
  };
  return routeMap[href] || href;
}
function transformMenuItem(label: string, href: string | null | undefined) {
  const normalizedHref = normalizeMenuHref(href);
  if (label === "Book a Demo Contact Sales") {
    return {
      label: "Login",
      href: "https://my.elorag.com",
      external: true
    };
  }
  if (label === "White Label for Agencies") {
    return {
      label: "White Label",
      href: normalizedHref,
      external: false
    };
  }
  if (label === "Security Governance") {
    return {
      label: "Security & Governance",
      href: normalizedHref,
      external: false
    };
  }
  return {
    label,
    href: normalizedHref,
    external: false
  };
}
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-1 lg:gap-2">
        {menuItems.map(item => {
        const transformed = transformMenuItem(item.label, item.href);
        return <NavigationMenuItem key={item.id}>
              <NavigationMenuLink asChild>
                {transformed.external ? <a href={transformed.href} target="_blank" rel="noopener noreferrer" className="group relative inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-[0.95rem] font-medium text-foreground/90 transition-colors hover:text-primary focus:text-primary outline-none">
                    {transformed.label}
                    <span className="absolute bottom-1 left-4 right-4 h-[2px] origin-left scale-x-0 bg-primary transition-transform duration-300 group-hover:scale-x-100" />
                  </a> : <Link to={transformed.href} className="group relative inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-[0.95rem] font-medium text-foreground/90 transition-colors hover:text-primary focus:text-primary outline-none">
                    {transformed.label}
                    <span className="absolute bottom-1 left-4 right-4 h-[2px] origin-left scale-x-0 bg-primary transition-transform duration-300 group-hover:scale-x-100" />
                  </Link>}
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  closeMenu
}: {
  closeMenu: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="mt-8 flex flex-col gap-4 px-4">
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="mt-8 flex flex-col gap-2 px-4">
      {menuItems.map(item => {
      const transformed = transformMenuItem(item.label, item.href);
      return transformed.external ? <a key={item.id} href={transformed.href} target="_blank" rel="noopener noreferrer" onClick={closeMenu} className="flex items-center border-b border-border/50 py-3 text-lg font-medium text-foreground hover:text-primary transition-colors">
            {transformed.label}
          </a> : <Link key={item.id} to={transformed.href} onClick={closeMenu} className="flex items-center border-b border-border/50 py-3 text-lg font-medium text-foreground hover:text-primary transition-colors">
            {transformed.label}
          </Link>;
    })}
    </nav>;
}
export default function EnterpriseHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (anchor.target === "_blank" || href.startsWith("http")) {
        setIsMobileMenuOpen(false);
        return;
      }
      if (href === "/#" || href === "/") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        setIsMobileMenuOpen(false);
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (!hashPart) return;
        if (path === window.location.pathname) {
          hash = `#${hashPart}`;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          const y = element.getBoundingClientRect().top + window.scrollY - 88;
          window.scrollTo({
            top: y,
            behavior: "smooth"
          });
          setIsMobileMenuOpen(false);
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  return <Fragment>
      <header id="enterprise-header" className={`sticky top-0 z-40 w-full transition-all duration-300 ${isScrolled ? "border-b border-border/80 bg-background/82 shadow-[0_8px_24px_-18px_rgba(0,0,0,0.12)] backdrop-blur-xl" : "border-b border-transparent bg-background/95"}`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-[76px] items-center justify-between">
            <div className="flex shrink-0 items-center">
              <Link to="/" className="flex items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1588799/img/elorag_logo.png" alt="EloRAG" className="h-8 w-auto md:h-10" />
              </Link>
            </div>

            <div className="hidden md:flex md:flex-1 md:justify-center">
              <MenuProvider menu_id="2">
                <HeaderMenuConsumer />
              </MenuProvider>
            </div>

            <div className="hidden md:flex md:items-center md:space-x-3">
              <Button variant="outline" asChild className="h-10 px-5 font-medium">
                <Link to="/book-a-demo">Book a Demo</Link>
              </Button>
              <Button asChild className="h-10 px-5 font-medium shadow-sm">
                <Link to="/pricing#transparent-subscription-plans">Start Subscription</Link>
              </Button>
            </div>

            <div className="flex items-center md:hidden">
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open main menu" className="text-foreground">
                <Menu className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && <div className="fixed inset-0 z-50 flex flex-col animate-in fade-in duration-200 bg-background md:hidden">
          <div className="flex h-[76px] items-center justify-between border-b border-border px-4 sm:px-6">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center outline-none">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1588799/img/elorag_logo.png" alt="EloRAG" className="h-8 w-auto" />
            </Link>

            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close main menu" className="text-foreground">
              <X className="h-6 w-6" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto pb-8">
            <MenuProvider menu_id="2">
              <MobileMenuConsumer closeMenu={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>

            <div className="mt-8 flex flex-col gap-4 px-4">
              <Button variant="outline" asChild className="h-12 w-full justify-center text-base">
                <Link to="/book-a-demo" onClick={() => setIsMobileMenuOpen(false)}>
                  Book a Demo
                </Link>
              </Button>
              <Button asChild className="h-12 w-full justify-center text-base shadow-sm">
                <Link to="/pricing#transparent-subscription-plans" onClick={() => setIsMobileMenuOpen(false)}>
                  Start Subscription
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </Fragment>;
}